import {
  format,
  parseISO,
  parse,
  isValid,
  isWithinInterval,
  addDays,
  subDays,
  endOfDay,
} from "date-fns";
import { de } from "date-fns/locale";

export const localTimezone: string =
  Intl.DateTimeFormat().resolvedOptions().timeZone;
export const deLocaleOption = { locale: de };

export const formatOverview = (date?: Date | string | null): string => {
  if (!date) {
    return "";
  }

  const value = new Date(date);
  return isValid(value) ? format(value, "PPP", deLocaleOption) : "";
};

export const formatPicker = (date: Date | string): string => {
  if (!date) {
    return "";
  }
  return format(new Date(date), "dd.MM.yyyy");
};
export const formatIso8601 = (date?: Date | string | null): string => {
  if (!date) {
    return "";
  }
  return format(new Date(date), "yyyy-MM-dd");
};

export const formatClockTime = (date: Date | string): string =>
  format(new Date(date), "HH:mm");

export const dueFormat = "MMMM d, yyyy h:mm aa";
export const formatDue = (date: Date): string =>
  format(new Date(date), dueFormat);
export const parseDue = (s: string): Date => parse(s, dueFormat, new Date());
export const formatBoardDue = (date: Date | string): string =>
  format(new Date(date), "yyyy-MM-dd");

export const parseBoardDue = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const candidates = [
    parseISO(value),
    parse(value, "yyyy-MM-dd", new Date()),
    parse(value, "dd.MM.yyyy", new Date()),
    parseDue(value),
  ];

  return candidates.find(candidate => isValid(candidate));
};

export const formatBoardDueLabel = (value?: string | null): string | null => {
  const date = parseBoardDue(value);
  return date ? formatPicker(date) : value ?? null;
};

export const formatBoardTimestamp = (value?: Date | string | null): string => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  return isValid(date) ? format(date, "Pp", deLocaleOption) : "";
};

export const parseIso = (date: Date | string): Date =>
  parseISO(new Date(date).toISOString());

export const parse2Date = (s: string): Date =>
  parse(s, "dd.MM.yyyy", new Date());

export const toTime = (date: Date): number => new Date(date).getTime();

export const dayOfYear = (date: Date): number =>
  Math.floor(
    (date.getTime() - parseISO(date.getFullYear().toString()).getTime()) /
      86400000
  ) + 1;
export const isWinterHoliday = (date: Date): boolean => {
  const day = dayOfYear(date);
  return day < 7 || day > 350; // dec 16 -> jan 6
};

const easterSundayFor = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);

  return new Date(year, 2, 22 + h + l - 7 * m);
};

export const isEasterHoliday = (date: Date): boolean => {
  const easterSunday = easterSundayFor(date.getFullYear());

  return isWithinInterval(date, {
    start: subDays(easterSunday, 6),
    end: endOfDay(addDays(easterSunday, 7)),
  });
};

export const dateOfYear = (year: number | null): Date | null => {
  if (!year) {
    return null;
  }
  const now = new Date();
  now.setFullYear(year);
  return now;
};

export const formatMinutes = (
  minutes: number,
  leadingZero: boolean = false
): string => {
  if (minutes < 0) {
    return "-:--";
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const hoursStr = leadingZero
    ? hours.toString().padStart(2, "0")
    : hours.toString();
  return `${hoursStr}:${mins.toFixed(0).toString().padStart(2, "0")}`;
};

export const parseMinutes = (s: string): number => {
  const [hours, minutes] = s.split(":").map(Number);
  return hours * 60 + minutes;
};

// in HH:MM or H:MM format
export const elapsedMinutes = (
  startTime?: string,
  endTime?: string
): number => {
  if (!startTime || !endTime) {
    return -1;
  }
  return parseMinutes(endTime) - parseMinutes(startTime);
};

export const formatDay = (num: number): string => {
  const s = Number.isInteger(num) ? num.toString() : num.toFixed(1);
  // strip ".0" from the end
  return s.replace(/\.0$/, "");
};

export const formatElapsedMillis = (ms: number): string => {
  const seconds = ms < 0 ? Math.ceil(ms / 1000) : Math.floor(ms / 1000);
  const milliseconds = Math.abs(ms % 1000);
  return `${seconds}.${milliseconds.toFixed(0).padStart(3, "0")}s`;
};
